import { ChevronDownIcon, ChevronUpIcon, CursorArrowRaysIcon, TrashIcon } from '@heroicons/react/24/outline'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { useShallow } from 'zustand/react/shallow'

import { ActionModal } from '@ui/components/core'
import { showToast } from '@ui/components/elements/ToastManager'
import { Instance, Stack } from '@ui/interfaces'
import axios from '@ui/lib/api/axiosHelper'
import { CreateInstanceModal } from '@ui/pages/manage/components/CreateInstanceModal'
import { useAuth } from '@ui/providers/AuthProvider'
import { useInstanceStore } from '@ui/stores'
import dayjs from 'dayjs'
import debounce from 'lodash/debounce'

type InstancesProps = {
  modalState: boolean
  setModalState: (modalState: boolean) => void
}

export const Instances = ({ modalState, setModalState }: InstancesProps) => {
  const auth = useAuth()
  const navigate = useNavigate()
  const { currentOrganization } = auth
  const orgId = currentOrganization!.id

  const [instances, setInstances] = useState([])
  const [deleteConfirmationModal, setDeleteConfirmationModal] = useState(false)
  const [selectedInstance, setSelectedInstance] = useState<Instance | null>(null)
  const [expandedInstances, setExpandedInstances] = useState<{ [key: number]: boolean }>({})
  const [stacks, setStacks] = useState<{ [key: number]: Stack[] }>({})
  const [editingInstance, setEditingInstance] = useState<{ id: number; field: 'name' | 'description' } | null>(null)
  const [editValue, setEditValue] = useState('')

  const { setSelectedInstanceKey } = useInstanceStore(
    useShallow((state) => ({
      setSelectedInstanceKey: state.setSelectedInstanceKey,
    }))
  )

  const setModalClosed = () => {
    setModalState(false)
  }

  const effectFired = useRef(false)
  useEffect(() => {
    if (!effectFired.current) {
      effectFired.current = true
      fetchInstances()
    }
  }, [])

  const fetchInstances = async () => {
    try {
      const response = await axios.get(`/v1/organizations/${orgId}/instances`)
      setInstances(response.data)
    } catch (error) {
      console.error('Error fetching instances:', error)
    }
  }

  const removeInstance = (instanceId: number) => {
    const configuration = {
      url: `/v1/organizations/${orgId}/instances/${instanceId}`,
    }
    axios
      .delete(configuration.url)
      .then(() => {
        showToast.success('Instance has been removed')

        fetchInstances()
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .catch((err: any) => {
        console.log('Failed to delete instance ', instanceId, ' from org ', orgId, ': ', err)
        showToast.error('Failed to remove member')
      })
  }

  const toggleInstanceExpansion = async (instanceId: number) => {
    setExpandedInstances((prev) => ({ ...prev, [instanceId]: !prev[instanceId] }))
    if (!stacks[instanceId]) {
      await fetchStacks(instanceId)
    }
  }

  const fetchStacks = async (instanceId: number) => {
    try {
      const response = await axios.get(`/v1/organizations/${orgId}/instances/${instanceId}/stacks`)
      setStacks((prev) => ({ ...prev, [instanceId]: response.data }))
    } catch (error) {
      console.error('Error fetching stacks:', error)
    }
  }

  const updateStackState = async (instanceId: number, stackUuid: string, newState: Stack['state']) => {
    try {
      await axios.patch(`/v1/organizations/${orgId}/instances/${instanceId}/stacks/${stackUuid}`, { state: newState })
      await fetchStacks(instanceId)
    } catch (error) {
      console.error('Error updating stack state:', error)
      showToast.error('Failed to update stack state')
    }
  }

  const debouncedSave = useCallback(
    debounce(async (instanceId: number, field: string, value: string) => {
      try {
        await axios.patch(`/v1/organizations/${orgId}/instances/${instanceId}`, {
          [field]: value,
        })
        await fetchInstances()
      } catch (error) {
        console.error('Error updating instance:', error)
        showToast.error('Failed to update instance')
      }
    }, 500),
    [orgId, fetchInstances]
  )

  const handleEdit = (instance: Instance, field: 'name' | 'description') => {
    setEditingInstance({ id: instance.id, field })
    setEditValue(instance[field])
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setEditValue(newValue)
    if (editingInstance) {
      debouncedSave(editingInstance.id, editingInstance.field, newValue)
    }
  }

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    const length = e.target.value.length
    e.target.setSelectionRange(length, length)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setEditingInstance(null)
    }
  }

  const selectInstance = (instance: Instance) => {
    setSelectedInstanceKey(instance.connect_key)
    navigate(`/instances/${instance.connect_key}/explore/events`)
  }

  return (
    <>
      <ActionModal
        title="Delete Instance"
        message="Are you sure you want to delete this instance?"
        handleAction={() => {
          removeInstance(selectedInstance?.id || 0)
          setDeleteConfirmationModal(false)
        }}
        actionIdentifier="Delete"
        open={deleteConfirmationModal}
        dismissAction={() => {
          setDeleteConfirmationModal(false)
        }}
        dismissIdentifier="Cancel"
      />
      <CreateInstanceModal
        fetchInstances={fetchInstances}
        modalState={modalState}
        setModalClosed={setModalClosed}
        orgId={orgId}
      />
      <div className="mt-5 flow-root h-full">
        <div className="h-full w-full overflow-x-auto">
          <div className="inline-block min-w-full align-middle">
            <div className="shadow ring-1 ring-black/5 sm:rounded-lg">
              <table className="min-w-full table-fixed divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="sticky top-0 z-10 w-1/6 border-b border-gray-300 bg-white p-3.5 text-left text-sm font-medium text-gray-900">
                      Name
                    </th>
                    <th
                      scope="col"
                      className="sticky top-0 z-10 w-1/3 border-b border-gray-300 bg-white p-3.5 text-left text-sm font-medium text-gray-900">
                      Description
                    </th>
                    <th
                      scope="col"
                      className="sticky top-0 z-10 w-1/6 border-b border-gray-300 bg-white p-3.5 text-left text-sm font-medium text-gray-900">
                      Last online
                    </th>
                    <th
                      scope="col"
                      className="sticky top-0 z-10 w-1/3 border-b border-gray-300 bg-white p-3.5 text-left text-sm font-medium text-gray-900">
                      Key
                    </th>
                    <th
                      scope="col"
                      className="sticky top-0 z-10 w-12 border-b border-gray-300 bg-white p-3.5 text-left text-sm font-semibold text-gray-900">
                      <span className="sr-only">Remove</span>
                    </th>
                    <th
                      scope="col"
                      className="sticky top-0 z-10 w-12 border-b border-gray-300 bg-white p-3.5 text-left text-sm font-semibold text-gray-900">
                      <span className="sr-only">Show Stacks</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {instances.map((instance: Instance) => (
                    <React.Fragment key={instance.id}>
                      <tr className="cursor-pointer" onClick={() => toggleInstanceExpansion(instance.id)}>
                        <td className="truncate p-3.5 text-sm font-normal text-gray-900">
                          {editingInstance?.id === instance.id && editingInstance.field === 'name' ? (
                            <input
                              type="text"
                              value={editValue}
                              onChange={handleChange}
                              onBlur={() => setEditingInstance(null)}
                              onClick={(e) => e.stopPropagation()}
                              onFocus={handleInputFocus}
                              onKeyDown={handleKeyDown}
                              autoFocus
                              className="w-full rounded-md border-gray-300 bg-white px-2 py-1 text-sm font-normal text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            />
                          ) : (
                            <span
                              onClick={(e) => {
                                e.stopPropagation()
                                handleEdit(instance, 'name')
                              }}>
                              {instance.name}
                            </span>
                          )}
                        </td>
                        <td className="truncate p-3.5 text-sm font-normal text-gray-900">
                          {editingInstance?.id === instance.id && editingInstance.field === 'description' ? (
                            <input
                              type="text"
                              value={editValue}
                              onChange={handleChange}
                              onBlur={() => setEditingInstance(null)}
                              onClick={(e) => e.stopPropagation()}
                              onFocus={handleInputFocus}
                              onKeyDown={handleKeyDown}
                              autoFocus
                              className="w-full rounded-md border-gray-300 bg-white px-2 py-1 text-sm font-normal text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            />
                          ) : (
                            <span
                              onClick={(e) => {
                                e.stopPropagation()
                                handleEdit(instance, 'description')
                              }}>
                              {instance.description}
                            </span>
                          )}
                        </td>
                        <td className="truncate p-3.5 text-sm font-medium text-gray-900">
                          {instance.last_heartbeat ? dayjs(instance.last_heartbeat).fromNow() : 'Never'}
                        </td>
                        <td className="truncate p-3.5 text-sm font-normal text-gray-900">{instance.connect_key}</td>
                        <td className="flex flex-row items-center gap-2 p-3.5 text-sm font-medium">
                          <a
                            className="group rounded-md p-1 transition-all duration-300 hover:bg-gray-100"
                            onClick={(e) => {
                              e.stopPropagation()
                              selectInstance(instance)
                            }}
                            title="Select Instance">
                            <CursorArrowRaysIcon
                              className="h-6 w-6 cursor-pointer stroke-gray-900 group-hover:stroke-gray-700"
                              aria-hidden="true"
                            />
                          </a>

                          <a
                            onClick={(e) => {
                              e.stopPropagation()
                              setSelectedInstance(instance)
                              setDeleteConfirmationModal(true)
                            }}
                            className="group rounded-md p-1 transition-all duration-300 hover:bg-red-100"
                            title="Delete Instance">
                            <TrashIcon
                              className="h-6 w-6 cursor-pointer stroke-red-800 group-hover:stroke-red-700"
                              aria-hidden="true"
                            />
                          </a>
                        </td>
                        <td className="p-3.5 text-sm font-normal text-gray-900">
                          {expandedInstances[instance.id] ? (
                            <ChevronUpIcon className="h-5 w-5" />
                          ) : (
                            <ChevronDownIcon className="h-5 w-5" />
                          )}
                        </td>
                      </tr>
                      {expandedInstances[instance.id] && (
                        <tr>
                          <td colSpan={6}>
                            <div className="px-4 py-2">
                              <h4 className="mb-2 text-lg font-semibold">Stacks</h4>
                              <table className="min-w-full table-fixed divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="w-1/5 p-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                      UUID
                                    </th>
                                    <th className="w-1/5 p-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                      State
                                    </th>
                                    <th className="w-1/5 p-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                      Action
                                    </th>
                                    <th className="w-1/5 p-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                      Git Ref
                                    </th>
                                    <th className="w-1/5 p-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                      Version
                                    </th>
                                    <th className="w-1/5 p-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                      Last Heartbeat
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 bg-white">
                                  {stacks[instance.id]?.map((stack) => (
                                    <tr key={stack.uuid} className={stack.state === 'active' ? 'bg-green-100' : ''}>
                                      <td className="truncate p-3 text-sm text-gray-500">{stack.uuid}</td>
                                      <td className="truncate p-3 text-sm text-gray-500">{stack.state}</td>
                                      <td className="p-3 text-sm text-gray-500">
                                        {stack.state === 'active' ? (
                                          <span className="font-semibold text-green-600">-</span>
                                        ) : stack.state === 'idle' ? (
                                          <button
                                            onClick={() => updateStackState(instance.id, stack.uuid, 'active')}
                                            className="rounded bg-blue-500 px-2 py-1 text-white hover:bg-blue-600">
                                            Activate
                                          </button>
                                        ) : stack.state === 'decommissioned' ? (
                                          <button
                                            onClick={() => updateStackState(instance.id, stack.uuid, 'active')}
                                            className="rounded bg-green-500 px-2 py-1 text-white hover:bg-green-600">
                                            Reactivate
                                          </button>
                                        ) : (
                                          stack.state
                                        )}
                                      </td>
                                      <td className="truncate p-3 text-sm text-gray-500">{stack.gitref}</td>
                                      <td className="truncate p-3 text-sm text-gray-500">{stack.version}</td>
                                      <td className="truncate p-3 text-sm text-gray-500">{stack.last_heartbeat}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
